import { POLLING_GRAPH } from '@/constants/polling';
import { QUERY_KEYS } from '@/constants/query';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPair } from '@/entities/WrappedPair';
import { ILiqChartConvertedData } from '@/types/chart';
import { useQuery } from '@tanstack/react-query';
import { BigNumber } from 'ethers';
import _ from 'lodash';
import { useMemo } from 'react';
import { fetchLiquidityData } from '../futures/api';

export function useQueryPairLiqChart(pair: WrappedPair | undefined) {
  const result = useQuery<ILiqChartConvertedData[] | null>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.EARN.LIQ_CHART(pair?.chainId, pair?.instrumentAddr, pair?.expiry),
    queryFn: async () => {
      if (!pair?.chainId || !pair?.instrumentAddr || !pair?.expiry) return null;
      const data = await fetchLiquidityData({
        chainId: pair.chainId,
        instrument: pair.instrumentAddr,
        expiry: pair.expiry,
      });
      if (!data) return null;
      return data.map((d) => {
        const bn = WrappedBigNumber.from(BigNumber.from(d.liquidity));
        const liqNumber = bn.toNumber();
        return {
          ...d,
          liqNumber,
        };
      });
    },
    refetchInterval: POLLING_GRAPH,
    enabled: Boolean(pair?.chainId && pair?.instrumentAddr && pair?.expiry),
  });

  const liqChartData = useMemo(() => {
    if (result.data && pair) {
      const orderedData = _.orderBy(
        result.data.map((item) => {
          return {
            ...item,
            price: item.price,
            pair: pair,
          };
        }),
        ['tick'],
      );
      return orderedData;
    }
    return [];
  }, [result.data, pair]);

  return {
    ...result,
    liqChartData: liqChartData,
  };
}
